import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeColor } from '../Theming';
import { ANALYTICS_PAGE_NAMES, useIsIPad } from '../Common/Analytics/utils';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import { isAndroid } from 'react-device-detect';
import { onClose } from '../TIM/KindredAndroid';

const Container = styled.div<{ isIPad?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  margin: 0 auto;
`;

const SuccessHero = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  isolation: isolate;
  padding: 112px 30px;
  color: #ffffff;
  text-align: center;

  &::before {
    position: absolute;
    z-index: -1;
    inset: 0;

    background: linear-gradient(
      119.92deg,
      #8a2387 10%,
      #7650aa 46%,
      #5276c8 86%,
      #15c1ff 125%
    );
    clip-path: url('#kindred-success-curve');
    content: '';
  }

  @media (min-width: 600px) {
    padding: clamp(136px, 16vw, 176px) 48px;
  }

  @media (max-width: 359px) {
    padding: 88px 20px;
  }

  @media (max-width: 599px) and (max-height: 700px) {
    padding: 64px 20px;
  }
`;

const CurveDefinition = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-family: 'Fira Sans', sans-serif;
  font-size: 30px;
  font-weight: 700;
  font-style: normal;
  line-height: 115%;
  letter-spacing: 0;

  span {
    display: block;
  }

  @media (min-width: 600px) {
    max-width: 760px;
    font-size: clamp(38px, 5.2vw, 48px);
  }

  @media (max-width: 359px) {
    font-size: 26px;
  }
`;

const Subtitle = styled.p`
  max-width: 360px;
  margin: 24px 0 0;
  font-family: 'Fira Sans', sans-serif;
  font-weight: 400;
  font-size: 18px;
  font-style: normal;
  line-height: 125%;
  letter-spacing: 0;

  span {
    display: block;
  }

  @media (min-width: 600px) {
    max-width: 620px;
    font-size: clamp(22px, 3vw, 26px);
  }

  @media (max-width: 359px) {
    max-width: 300px;
    margin-top: 20px;
    font-size: 16px;
  }
`;

const PopularBrands = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const BottomContent = styled.div<{ isIPad?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 16px 24px 34px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;

  & > a {
    color: #46555f;
    margin-top: 20px;
    font-size: ${({ isIPad }) => (isIPad ? '20px' : '16px')};
    text-align: center;
  }

  @media (min-width: 600px) {
    padding: 32px 48px 48px;
  }
`;

const Button = styled.button<{ isIPad?: boolean }>`
  background-color: #52ae67;
  height: ${({ isIPad }) => (isIPad ? '70px' : '61px')};
  color: ${themeColor('white')};
  font-size: ${({ isIPad }) => (isIPad ? '24px' : '24px')};
  font-weight: 600;
  width: 100%;
  max-width: 640px;
  font-style: semi-bold;
  border: none;

  @media (max-width: 359px) {
    height: 56px;
    font-size: 20px;
  }
`;

const KindredPluginActivationSuccess = () => {
  const { t } = useTranslation();
  const { activation_complete_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(activation_complete_page, 'Kindred');
  const query = useQuery();
  const origin = query.get('origin');
  const isIPad = useIsIPad();

  const openKindredApp = () => {
    if (isAndroid) {
      return onClose();
    }
    if (origin) {
      window.location.href = `${origin}://`;
    }
  };

  return (
    <Container isIPad={isIPad}>
      <CurveDefinition aria-hidden="true">
        <defs>
          <clipPath
            id="kindred-success-curve"
            clipPathUnits="objectBoundingBox"
          >
            <path d="M 0 0 H 1 V 0.72 Q 0.5 1, 0 0.72 Z" />
          </clipPath>
        </defs>
      </CurveDefinition>
      <SuccessHero>
        <Title>
          <span>{t('KindredPluginActivation.success.allDone')}</span>
          <span>{t('KindredPluginActivation.success.checkout')}</span>
        </Title>
        <Subtitle>
          <span>{t('KindredPluginActivation.success.popup')}</span>
          <span>{t('KindredPluginActivation.success.discountCodes')}</span>
        </Subtitle>
      </SuccessHero>
      <PopularBrands
        src={require('../Assets/KindredPluginActivation/popularBrands.png')}
        alt=""
        aria-hidden="true"
      />
      <BottomContent isIPad={isIPad}>
        <Button isIPad={isIPad} onClick={openKindredApp}>
          {t('KindredPluginActivation.success.backToApp')}
        </Button>
        <a
          target="_blank"
          href="https://help.kindred.co/support/solutions"
          rel="noreferrer"
          className="faq"
        >
          {t('KindredPluginActivation.success.faq')}
        </a>
      </BottomContent>
    </Container>
  );
};

export default KindredPluginActivationSuccess;
